import React from "react";

/**
 * Wraps a dynamic import with retry logic for handling transient network failures.
 * Useful for code-split chunks that may fail to load on mobile devices.
 *
 * @param importFn - The dynamic import function to execute
 * @param retries - Number of retry attempts (default: 3)
 * @param delay - Base delay in milliseconds for exponential backoff (default: 1000ms)
 * @returns Promise that resolves to the imported module
 *
 * @example
 * ```typescript
 * const module = await importWithRetry(() => import('./my-module'));
 * ```
 */
export async function importWithRetry<T>(
  importFn: () => Promise<T>,
  retries = 3,
  delay = 1000,
): Promise<T> {
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      return await importFn();
    } catch (error) {
      const isLastAttempt = attempt === retries - 1;
      if (isLastAttempt) {
        window.location.reload();
        throw error;
      }

      // Exponential backoff: 1s, 2s, 3s
      await new Promise((resolve) => setTimeout(resolve, delay * (attempt + 1)));
    }
  }
  throw new Error("Import failed after retries");
}

/**
 * Creates a lazy-loaded React component with retry logic.
 * Useful for route-based code splitting that may fail on mobile devices.
 *
 * @param importFn - The dynamic import function that returns a module with default export
 * @returns A lazy-loaded React component
 *
 * @example
 * ```typescript
 * const MyComponent = lazyWithRetry(() => import('./MyComponent'));
 * ```
 */
export function lazyWithRetry<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return React.lazy(() => importWithRetry(importFn));
}
